import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  SelectChangeEvent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Интерфейс для роли
interface ManagerRole {
  role_id: number;
  role: string;
  description: string;
  created_at: string;
}

// Mock data
const MOCK_ROLES: ManagerRole[] = [];

const ManagerRolesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Состояния для диалогов
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<ManagerRole | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Форма для создания/редактирования роли
  const [roleForm, setRoleForm] = useState({
    role: '',
    description: 'active'
  });

  // Mock data fetching
  const data = { roles: MOCK_ROLES };
  const createLoading = false;
  const updateLoading = false;
  const deleteLoading = false;

  // Mock mutations
  const handleCreateSubmit = async () => {
    try {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setSuccessMessage('Роль успешно создана');
      handleCloseCreateDialog();
      setLoading(false);
    } catch (error) {
      setErrorMessage('Ошибка при создании роли');
    }
  };

  const handleEditSubmit = async () => {
    try {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setSuccessMessage('Роль успешно обновлена');
      handleCloseEditDialog();
      setLoading(false);
    } catch (error) {
      setErrorMessage('Ошибка при обновлении роли');
    }
  };

  const handleDeleteSubmit = async () => {
    try {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setSuccessMessage('Роль успешно удалена');
      handleCloseDeleteDialog();
      setLoading(false);
    } catch (error) {
      setErrorMessage('Ошибка при удалении роли');
    }
  };
  
  // Keep all your existing handlers
  const handleOpenCreateDialog = () => {
    setRoleForm({ role: '', description: 'active' });
    setOpenCreateDialog(true);
  };
  
  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };
  
  const handleOpenEditDialog = (role: ManagerRole) => {
    setSelectedRole(role);
    setRoleForm({
      role: role.role,
      description: role.description || 'active'
    });
    setOpenEditDialog(true);
  };
  
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedRole(null);
  };
  
  const handleOpenDeleteDialog = (role: ManagerRole) => {
    setSelectedRole(role);
    setOpenDeleteDialog(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedRole(null);
  };
  
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string, value: unknown }> | SelectChangeEvent) => {
    const { name, value } = e.target;
    setRoleForm(prev => ({
      ...prev,
      [name as string]: value
    }));
  };
  
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRoleForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setRoleForm(prev => ({
      ...prev,
      [name as string]: value
    }));
  };
  
  const renderStatus = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip label="Активна" color="success" size="small" />;
      case 'inactive':
        return <Chip label="Неактивна" color="default" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  // Keep your existing JSX return
  return (
    <Box>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Роли менеджеров</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={handleOpenCreateDialog}
        >
          Добавить роль
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">
          Произошла ошибка при загрузке данных: {error}
        </Alert>
      ) : data?.roles?.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.roles.map((role: ManagerRole) => (
                <TableRow key={role.role_id}>
                  <TableCell>{role.role_id}</TableCell>
                  <TableCell>{role.role}</TableCell>
                  <TableCell>{renderStatus(role.description || 'active')}</TableCell>
                  <TableCell align="right">
                    <Button
                      startIcon={<EditIcon />}
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => handleOpenEditDialog(role)}
                    >
                      Редактировать
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      size="small"
                      color="error"
                      onClick={() => handleOpenDeleteDialog(role)}
                    >
                      Удалить
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">Нет данных для отображения</Alert>
      )}
      
      {/* Keep all your existing dialogs */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog}>
        <DialogTitle>Добавить новую роль</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название роли"
                name="role"
                value={roleForm.role}
                onChange={handleTextChange}
                required
                error={roleForm.role.trim() === ''}
                helperText={roleForm.role.trim() === '' ? 'Обязательное поле' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Статус</InputLabel>
                <Select
                  labelId="status-label"
                  label="Статус"
                  name="description"
                  value={roleForm.description}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="active">Активна</MenuItem>
                  <MenuItem value="inactive">Неактивна</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Отмена</Button>
          <Button 
            onClick={handleCreateSubmit} 
            variant="contained" 
            disabled={createLoading}
          >
            {createLoading ? <CircularProgress size={24} /> : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle>Редактировать роль</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название роли"
                name="role"
                value={roleForm.role}
                onChange={handleTextChange}
                required
                error={roleForm.role.trim() === ''}
                helperText={roleForm.role.trim() === '' ? 'Обязательное поле' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-status-label">Статус</InputLabel>
                <Select
                  labelId="edit-status-label"
                  label="Статус"
                  name="description"
                  value={roleForm.description}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="active">Активна</MenuItem>
                  <MenuItem value="inactive">Неактивна</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Отмена</Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            disabled={updateLoading}
          >
            {updateLoading ? <CircularProgress size={24} /> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Удалить роль</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы действительно хотите удалить роль "{selectedRole?.role}"? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Отмена</Button>
          <Button 
            onClick={handleDeleteSubmit} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagerRolesPage; 